import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useLanguage } from '@/lib/context';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import {
  Loader2, ChevronLeft, ChevronRight, Home, MapPin, DollarSign,
  Settings, Image, Check, Wifi, Car, Coffee, Tv, Dumbbell,
  ShieldCheck, PawPrint, Cigarette, Building2, Zap, Upload, X, GripVertical
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Photo Upload Section Component
 * Supports drag-drop, multi-file upload, reordering, and URL fallback
 */
const PhotoUploadSection = ({ photos, onPhotosChange, t }) => {
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [urlInput, setUrlInput] = useState('');
  const fileInputRef = useRef(null);
  const token = localStorage.getItem('provider_token');

  const MAX_PHOTOS = 10;
  const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
  const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFiles(Array.from(e.dataTransfer.files));
    }
  }, [photos]);

  const handleFiles = async (files) => {
    if (photos.length >= MAX_PHOTOS) {
      toast.error(`Maximum ${MAX_PHOTOS} photos allowed`);
      return;
    }

    const validFiles = files.filter(file => {
      if (!ALLOWED_TYPES.includes(file.type)) {
        toast.error(`${file.name}: Invalid file type. Use JPG, PNG, or WebP`);
        return false;
      }
      if (file.size > MAX_FILE_SIZE) {
        toast.error(`${file.name}: File too large. Maximum 10MB`);
        return false;
      }
      return true;
    });

    const remainingSlots = MAX_PHOTOS - photos.length;
    const filesToUpload = validFiles.slice(0, remainingSlots);

    if (filesToUpload.length === 0) return;

    setUploading(true);
    const uploadedUrls = [];

    for (const file of filesToUpload) {
      try {
        const formData = new FormData();
        formData.append('file', file);

        const response = await fetch(`${API_URL}/api/accommodation/host/photos/upload`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          },
          body: formData
        });

        if (!response.ok) {
          const error = await response.json();
          toast.error(`${file.name}: ${error.detail || 'Upload failed'}`);
          continue;
        }

        const data = await response.json();
        uploadedUrls.push(data.url);
      } catch (error) {
        console.error('Upload error:', error);
        toast.error(`${file.name}: Upload failed`);
      }
    }

    if (uploadedUrls.length > 0) {
      onPhotosChange([...photos, ...uploadedUrls]);
      toast.success(`${uploadedUrls.length} photo${uploadedUrls.length > 1 ? 's' : ''} uploaded`);
    }

    setUploading(false);
  };

  const handleFileInput = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      handleFiles(Array.from(e.target.files));
    }
  };

  const addPhotoUrl = () => {
    const url = urlInput.trim();
    if (!url) return;
    
    if (photos.length >= MAX_PHOTOS) {
      toast.error(`Maximum ${MAX_PHOTOS} photos allowed`);
      return;
    }

    if (photos.includes(url)) {
      toast.error('This URL is already added');
      return;
    }

    // Basic URL validation
    try {
      new URL(url);
    } catch {
      toast.error('Please enter a valid URL');
      return;
    }

    onPhotosChange([...photos, url]);
    setUrlInput('');
  };

  const removePhoto = (index) => {
    onPhotosChange(photos.filter((_, i) => i !== index));
  };

  const movePhoto = (fromIndex, toIndex) => {
    if (toIndex < 0 || toIndex >= photos.length) return;
    const newPhotos = [...photos];
    const [removed] = newPhotos.splice(fromIndex, 1);
    newPhotos.splice(toIndex, 0, removed);
    onPhotosChange(newPhotos);
  };

  return (
    <div className="space-y-6">
      {/* Drag & Drop Zone */}
      <div
        className={`relative border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
          dragActive 
            ? 'border-purple-500 bg-purple-50' 
            : 'border-gray-300 hover:border-gray-400'
        } ${uploading ? 'pointer-events-none opacity-50' : ''}`}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          multiple
          onChange={handleFileInput}
          className="hidden"
          data-testid="photo-file-input"
        />
        
        {uploading ? (
          <div className="flex flex-col items-center">
            <Loader2 className="h-12 w-12 text-purple-500 animate-spin mb-4" />
            <p className="text-gray-600">Uploading photos...</p>
          </div>
        ) : (
          <>
            <Upload className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="font-medium text-gray-700 mb-2">
              {t('accommodation.createListing.uploadPhotos')}
            </h3>
            <p className="text-sm text-gray-500 mb-4">
              Drag and drop photos here, or click to browse
            </p>
            <Button 
              type="button" 
              variant="outline"
              onClick={() => fileInputRef.current?.click()}
              data-testid="upload-photos-btn"
            >
              <Image className="h-4 w-4 mr-2" />
              Choose Files
            </Button>
            <p className="text-xs text-gray-400 mt-4">
              {t('accommodation.createListing.photosHint')} • JPG, PNG, WebP • Max 10MB each
            </p>
          </>
        )}
      </div>

      {/* Photo Grid */}
      {photos.length > 0 && (
        <div>
          <Label className="mb-2 block">
            Photos ({photos.length}/{MAX_PHOTOS})
            <span className="text-gray-400 text-xs ml-2">Drag to reorder</span>
          </Label>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {photos.map((url, idx) => (
              <div 
                key={idx} 
                className="relative aspect-video group rounded-lg overflow-hidden border"
              >
                <img 
                  src={url} 
                  alt={`Photo ${idx + 1}`}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.target.src = 'https://via.placeholder.com/400x300?text=Invalid+URL';
                  }}
                />
                
                {/* Photo Controls Overlay */}
                <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                  <Button
                    type="button"
                    size="sm"
                    variant="secondary"
                    onClick={() => movePhoto(idx, idx - 1)}
                    disabled={idx === 0}
                    className="h-8 w-8 p-0"
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="secondary"
                    onClick={() => movePhoto(idx, idx + 1)}
                    disabled={idx === photos.length - 1}
                    className="h-8 w-8 p-0"
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="destructive"
                    onClick={() => removePhoto(idx)}
                    className="h-8 w-8 p-0"
                    data-testid={`remove-photo-${idx}`}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
                
                {/* First photo badge */}
                {idx === 0 && (
                  <Badge className="absolute top-2 left-2 bg-purple-600">
                    Cover Photo
                  </Badge>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* URL Fallback Input */}
      <div className="pt-4 border-t">
        <Label className="text-sm text-gray-500">Or add photo URL</Label>
        <div className="flex gap-2 mt-1">
          <Input
            value={urlInput}
            onChange={(e) => setUrlInput(e.target.value)}
            placeholder="https://example.com/photo.jpg"
            onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addPhotoUrl())}
            data-testid="photo-url-input"
          />
          <Button type="button" variant="outline" onClick={addPhotoUrl}>
            Add URL
          </Button>
        </div>
      </div>
    </div>
  );
};

const ACCOMMODATION_TYPES = [
  { value: 'entire_apartment', label: 'Entire Apartment' },
  { value: 'private_room', label: 'Private Room' },
  { value: 'shared_room', label: 'Shared Room' },
  { value: 'company_housing', label: 'Company Housing' },
  { value: 'worker_dorm', label: 'Worker Dorm' },
];

const COUNTRIES = [
  { code: 'EE', name: 'Estonia' },
  { code: 'FI', name: 'Finland' },
  { code: 'SE', name: 'Sweden' },
  { code: 'NO', name: 'Norway' },
  { code: 'LV', name: 'Latvia' },
  { code: 'LT', name: 'Lithuania' },
  { code: 'DE', name: 'Germany' },
  { code: 'DK', name: 'Denmark' },
  { code: 'PL', name: 'Poland' },
];

const STANDARD_AMENITIES = [
  { id: 'wifi', name: 'Wi-Fi', icon: Wifi },
  { id: 'parking', name: 'Parking', icon: Car },
  { id: 'kitchen', name: 'Kitchen', icon: Coffee },
  { id: 'workspace', name: 'Workspace', icon: Home },
  { id: 'heating', name: 'Heating', icon: Home },
  { id: 'air_conditioning', name: 'A/C', icon: Home },
  { id: 'tv', name: 'TV', icon: Tv },
  { id: 'laundry', name: 'Laundry', icon: Home },
  { id: 'breakfast', name: 'Breakfast', icon: Coffee },
  { id: 'elevator', name: 'Elevator', icon: Building2 },
  { id: 'gym', name: 'Gym', icon: Dumbbell },
  { id: 'security', name: '24/7 Security', icon: ShieldCheck },
  { id: 'pets_allowed', name: 'Pets Allowed', icon: PawPrint },
  { id: 'smoking_allowed', name: 'Smoking Allowed', icon: Cigarette },
];

const STEPS = [
  { id: 'basic', title: 'Basic Info', icon: Home },
  { id: 'location', title: 'Location', icon: MapPin },
  { id: 'pricing', title: 'Pricing', icon: DollarSign },
  { id: 'details', title: 'Details', icon: Settings },
  { id: 'photos', title: 'Photos', icon: Image },
];

const CreateListingPage = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [currentStep, setCurrentStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  
  const token = localStorage.getItem('provider_token');

  // Form State
  const [formData, setFormData] = useState({
    // Basic Info
    title: '',
    description: '',
    accommodation_type: 'entire_apartment',
    
    // Location
    country: 'FI',
    city: '',
    area: '',
    address: '',
    
    // Pricing
    nightly_price: '',
    weekly_discount_percent: '',
    monthly_discount_percent: '',
    currency: 'EUR',
    
    // Details
    max_occupants: '2',
    bedrooms: '1',
    beds: '1',
    bathrooms: '1',
    amenities: ['wifi', 'heating'],
    house_rules: '',
    check_in_time: '15:00',
    check_out_time: '11:00',
    booking_mode: 'request',
    min_nights: '1',
    max_nights: '365',
    work_friendly: true,
    
    // Photos
    photos: []
  });

  useEffect(() => {
    if (!token) {
      navigate('/provider/login');
    }
  }, [token]);

  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const toggleAmenity = (amenityId) => {
    setFormData(prev => ({
      ...prev,
      amenities: prev.amenities.includes(amenityId)
        ? prev.amenities.filter(a => a !== amenityId)
        : [...prev.amenities, amenityId]
    }));
  };

  const validateStep = () => {
    switch (currentStep) {
      case 0: // Basic Info
        if (!formData.title || formData.title.length < 5) {
          toast.error('Please enter a title (minimum 5 characters)');
          return false;
        }
        if (!formData.description || formData.description.length < 20) {
          toast.error('Please enter a description (minimum 20 characters)');
          return false;
        }
        return true;
      case 1: // Location
        if (!formData.city) {
          toast.error('Please enter a city');
          return false;
        }
        if (!formData.address || formData.address.length < 5) {
          toast.error('Please enter a valid address');
          return false;
        }
        return true;
      case 2: // Pricing
        if (!formData.nightly_price || parseFloat(formData.nightly_price) <= 0) {
          toast.error('Please enter a valid nightly price');
          return false;
        }
        return true;
      case 3: // Details
        if (!formData.max_occupants || parseInt(formData.max_occupants) < 1) {
          toast.error('Please enter maximum occupants');
          return false;
        }
        return true;
      case 4: // Photos
        return true; // Photos are optional
      default:
        return true;
    }
  };

  const handleNext = () => {
    if (validateStep()) {
      setCurrentStep(prev => Math.min(prev + 1, STEPS.length - 1));
    }
  };

  const handleBack = () => {
    setCurrentStep(prev => Math.max(prev - 1, 0));
  };

  const handleSubmit = async () => {
    if (!validateStep()) return;
    
    setSubmitting(true);
    try {
      const payload = {
        title: formData.title,
        description: formData.description,
        accommodation_type: formData.accommodation_type,
        country: formData.country,
        city: formData.city,
        area: formData.area || null,
        address: formData.address,
        nightly_price: parseFloat(formData.nightly_price),
        weekly_discount_percent: formData.weekly_discount_percent ? parseFloat(formData.weekly_discount_percent) : null,
        monthly_discount_percent: formData.monthly_discount_percent ? parseFloat(formData.monthly_discount_percent) : null,
        currency: formData.currency,
        max_occupants: parseInt(formData.max_occupants),
        bedrooms: formData.bedrooms ? parseInt(formData.bedrooms) : null,
        beds: formData.beds ? parseInt(formData.beds) : null,
        bathrooms: formData.bathrooms ? parseFloat(formData.bathrooms) : null,
        amenities: formData.amenities,
        house_rules: formData.house_rules || null,
        check_in_time: formData.check_in_time,
        check_out_time: formData.check_out_time,
        booking_mode: formData.booking_mode,
        min_nights: parseInt(formData.min_nights),
        max_nights: parseInt(formData.max_nights),
        photos: formData.photos,
        work_friendly: formData.work_friendly
      };

      const response = await fetch(`${API_URL}/api/accommodation/host/listings`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Failed to create listing');
      }

      toast.success(t('accommodation.createListing.success'));
      navigate('/host/dashboard');
    } catch (error) {
      console.error('Error creating listing:', error);
      toast.error(error.message);
    } finally {
      setSubmitting(false);
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 0: // Basic Info
        return (
          <div className="space-y-6">
            <div>
              <Label>{t('accommodation.createListing.listingTitle')} *</Label>
              <Input
                value={formData.title}
                onChange={(e) => updateField('title', e.target.value)}
                placeholder={t('accommodation.createListing.titlePlaceholder')}
                maxLength={100}
                data-testid="listing-title"
              />
              <p className="text-xs text-gray-500 mt-1">{formData.title.length}/100 characters</p>
            </div>
            
            <div>
              <Label>{t('accommodation.createListing.description')} *</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => updateField('description', e.target.value)}
                placeholder={t('accommodation.createListing.descriptionPlaceholder')}
                rows={5}
                maxLength={2000}
                data-testid="listing-description"
              />
              <p className="text-xs text-gray-500 mt-1">{formData.description.length}/2000 characters</p>
            </div>
            
            <div>
              <Label>{t('accommodation.createListing.type')} *</Label>
              <Select value={formData.accommodation_type} onValueChange={(v) => updateField('accommodation_type', v)}>
                <SelectTrigger data-testid="listing-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ACCOMMODATION_TYPES.map(type => (
                    <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        );

      case 1: // Location
        return (
          <div className="space-y-6">
            <div>
              <Label>{t('accommodation.createListing.country')} *</Label>
              <Select value={formData.country} onValueChange={(v) => updateField('country', v)}>
                <SelectTrigger data-testid="listing-country">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {COUNTRIES.map(country => (
                    <SelectItem key={country.code} value={country.code}>{country.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>{t('accommodation.createListing.city')} *</Label>
              <Input
                value={formData.city}
                onChange={(e) => updateField('city', e.target.value)}
                placeholder="Helsinki"
                data-testid="listing-city"
              />
            </div>
            
            <div>
              <Label>{t('accommodation.createListing.area')}</Label>
              <Input
                value={formData.area}
                onChange={(e) => updateField('area', e.target.value)}
                placeholder="Kallio, Sörnäinen..."
                data-testid="listing-area"
              />
            </div>
            
            <div>
              <Label>{t('accommodation.createListing.address')} *</Label>
              <Input
                value={formData.address}
                onChange={(e) => updateField('address', e.target.value)}
                placeholder="Mannerheimintie 1, 00100"
                data-testid="listing-address"
              />
              <p className="text-xs text-gray-500 mt-1">
                Address is only shared with guests after booking is confirmed.
              </p>
            </div>
          </div>
        );

      case 2: // Pricing
        return (
          <div className="space-y-6">
            <div>
              <Label>{t('accommodation.createListing.nightlyPrice')} (€) *</Label>
              <Input
                type="number"
                min="1"
                step="0.01"
                value={formData.nightly_price}
                onChange={(e) => updateField('nightly_price', e.target.value)}
                placeholder="50"
                data-testid="listing-price"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>{t('accommodation.createListing.weeklyDiscount')}</Label>
                <Input
                  type="number"
                  min="0"
                  max="50"
                  value={formData.weekly_discount_percent}
                  onChange={(e) => updateField('weekly_discount_percent', e.target.value)}
                  placeholder="10"
                />
                <p className="text-xs text-gray-500 mt-1">For 7+ night stays</p>
              </div>
              <div>
                <Label>{t('accommodation.createListing.monthlyDiscount')}</Label>
                <Input
                  type="number"
                  min="0"
                  max="70"
                  value={formData.monthly_discount_percent}
                  onChange={(e) => updateField('monthly_discount_percent', e.target.value)}
                  placeholder="20"
                />
                <p className="text-xs text-gray-500 mt-1">For 30+ night stays</p>
              </div>
            </div>

            {/* Price Preview */}
            {formData.nightly_price && (
              <Card className="bg-purple-50 border-purple-100">
                <CardContent className="p-4">
                  <h4 className="font-medium text-purple-900 mb-2">Price Preview</h4>
                  <div className="space-y-1 text-sm">
                    <p>1 night: €{parseFloat(formData.nightly_price).toFixed(2)}</p>
                    {formData.weekly_discount_percent && (
                      <p className="text-green-600">
                        7 nights: €{(parseFloat(formData.nightly_price) * 7 * (1 - parseFloat(formData.weekly_discount_percent)/100)).toFixed(2)}
                        {' '}({formData.weekly_discount_percent}% off)
                      </p>
                    )}
                    {formData.monthly_discount_percent && (
                      <p className="text-green-600">
                        30 nights: €{(parseFloat(formData.nightly_price) * 30 * (1 - parseFloat(formData.monthly_discount_percent)/100)).toFixed(2)}
                        {' '}({formData.monthly_discount_percent}% off)
                      </p>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        );

      case 3: // Details
        return (
          <div className="space-y-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <Label>{t('accommodation.createListing.maxGuests')} *</Label>
                <Select value={formData.max_occupants} onValueChange={(v) => updateField('max_occupants', v)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[1,2,3,4,5,6,7,8,10,12,15,20].map(n => (
                      <SelectItem key={n} value={String(n)}>{n}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>{t('accommodation.createListing.bedrooms')}</Label>
                <Select value={formData.bedrooms} onValueChange={(v) => updateField('bedrooms', v)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[0,1,2,3,4,5,6].map(n => (
                      <SelectItem key={n} value={String(n)}>{n === 0 ? 'Studio' : n}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>{t('accommodation.createListing.beds')}</Label>
                <Select value={formData.beds} onValueChange={(v) => updateField('beds', v)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[1,2,3,4,5,6,7,8,10].map(n => (
                      <SelectItem key={n} value={String(n)}>{n}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>{t('accommodation.createListing.bathrooms')}</Label>
                <Select value={formData.bathrooms} onValueChange={(v) => updateField('bathrooms', v)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {['0.5', '1', '1.5', '2', '2.5', '3', '4'].map(n => (
                      <SelectItem key={n} value={n}>{n}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Amenities */}
            <div>
              <Label className="mb-3 block">{t('accommodation.createListing.amenities')}</Label>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                {STANDARD_AMENITIES.map(amenity => {
                  const Icon = amenity.icon;
                  const isSelected = formData.amenities.includes(amenity.id);
                  return (
                    <button
                      key={amenity.id}
                      type="button"
                      onClick={() => toggleAmenity(amenity.id)}
                      className={`flex items-center gap-2 p-3 rounded-lg border transition-all ${
                        isSelected 
                          ? 'bg-purple-50 border-purple-300 text-purple-700' 
                          : 'bg-white border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <Icon className="h-4 w-4" />
                      <span className="text-sm">{amenity.name}</span>
                      {isSelected && <Check className="h-4 w-4 ml-auto" />}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* House Rules */}
            <div>
              <Label>{t('accommodation.createListing.houseRules')}</Label>
              <Textarea
                value={formData.house_rules}
                onChange={(e) => updateField('house_rules', e.target.value)}
                placeholder="No smoking indoors, quiet hours after 10pm..."
                rows={3}
              />
            </div>

            {/* Check-in/out times */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>{t('accommodation.createListing.checkInTime')}</Label>
                <Input
                  type="time"
                  value={formData.check_in_time}
                  onChange={(e) => updateField('check_in_time', e.target.value)}
                />
              </div>
              <div>
                <Label>{t('accommodation.createListing.checkOutTime')}</Label>
                <Input
                  type="time"
                  value={formData.check_out_time}
                  onChange={(e) => updateField('check_out_time', e.target.value)}
                />
              </div>
            </div>

            {/* Booking Mode */}
            <div>
              <Label className="mb-3 block">{t('accommodation.createListing.bookingMode')}</Label>
              <div className="space-y-3">
                <label className={`flex items-start gap-3 p-4 rounded-lg border cursor-pointer ${
                  formData.booking_mode === 'instant' ? 'bg-green-50 border-green-300' : 'hover:bg-gray-50'
                }`}>
                  <input
                    type="radio"
                    name="booking_mode"
                    value="instant"
                    checked={formData.booking_mode === 'instant'}
                    onChange={() => updateField('booking_mode', 'instant')}
                    className="mt-1"
                  />
                  <div>
                    <div className="flex items-center gap-2">
                      <Zap className="h-4 w-4 text-green-600" />
                      <span className="font-medium">{t('accommodation.createListing.instant')}</span>
                    </div>
                    <p className="text-sm text-gray-500">{t('accommodation.createListing.instantDesc')}</p>
                  </div>
                </label>
                
                <label className={`flex items-start gap-3 p-4 rounded-lg border cursor-pointer ${
                  formData.booking_mode === 'request' ? 'bg-purple-50 border-purple-300' : 'hover:bg-gray-50'
                }`}>
                  <input
                    type="radio"
                    name="booking_mode"
                    value="request"
                    checked={formData.booking_mode === 'request'}
                    onChange={() => updateField('booking_mode', 'request')}
                    className="mt-1"
                  />
                  <div>
                    <span className="font-medium">{t('accommodation.createListing.request')}</span>
                    <p className="text-sm text-gray-500">{t('accommodation.createListing.requestDesc')}</p>
                  </div>
                </label>
              </div>
            </div>

            {/* Work Friendly */}
            <div className="flex items-center justify-between p-4 bg-blue-50 rounded-lg">
              <div>
                <Label className="font-medium">{t('accommodation.createListing.workFriendly')}</Label>
                <p className="text-sm text-gray-500">{t('accommodation.createListing.workFriendlyDesc')}</p>
              </div>
              <Switch
                checked={formData.work_friendly}
                onCheckedChange={(checked) => updateField('work_friendly', checked)}
              />
            </div>

            {/* Min/Max Nights */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>{t('accommodation.createListing.minNights')}</Label>
                <Input
                  type="number"
                  min="1"
                  value={formData.min_nights}
                  onChange={(e) => updateField('min_nights', e.target.value)}
                />
              </div>
              <div>
                <Label>{t('accommodation.createListing.maxNights')}</Label>
                <Input
                  type="number"
                  min="1"
                  max="365"
                  value={formData.max_nights}
                  onChange={(e) => updateField('max_nights', e.target.value)}
                />
              </div>
            </div>
          </div>
        );

      case 4: // Photos
        return (
          <PhotoUploadSection 
            photos={formData.photos}
            onPhotosChange={(photos) => updateField('photos', photos)}
            t={t}
          />
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b">
        <div className="max-w-3xl mx-auto px-4 py-4">
          <Button variant="ghost" size="sm" asChild>
            <Link to="/host/dashboard">
              <ChevronLeft className="h-4 w-4 mr-1" />
              Back to Dashboard
            </Link>
          </Button>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-8">
        <Card>
          <CardHeader>
            <CardTitle>{t('accommodation.createListing.title')}</CardTitle>
            <CardDescription>
              Step {currentStep + 1} of {STEPS.length}: {STEPS[currentStep].title}
            </CardDescription>
          </CardHeader>

          {/* Progress Steps */}
          <div className="px-6">
            <div className="flex items-center justify-between mb-6">
              {STEPS.map((step, idx) => {
                const Icon = step.icon;
                const isActive = idx === currentStep;
                const isComplete = idx < currentStep;
                
                return (
                  <React.Fragment key={step.id}>
                    <button
                      onClick={() => idx < currentStep && setCurrentStep(idx)}
                      disabled={idx > currentStep}
                      className={`flex flex-col items-center gap-1 ${
                        idx <= currentStep ? 'cursor-pointer' : 'cursor-not-allowed opacity-50'
                      }`}
                    >
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                        isComplete ? 'bg-green-500 text-white' :
                        isActive ? 'bg-purple-600 text-white' :
                        'bg-gray-200 text-gray-500'
                      }`}>
                        {isComplete ? <Check className="h-5 w-5" /> : <Icon className="h-5 w-5" />}
                      </div>
                      <span className={`text-xs ${isActive ? 'text-purple-600 font-medium' : 'text-gray-500'}`}>
                        {step.title}
                      </span>
                    </button>
                    {idx < STEPS.length - 1 && (
                      <div className={`flex-1 h-0.5 mx-2 ${idx < currentStep ? 'bg-green-500' : 'bg-gray-200'}`} />
                    )}
                  </React.Fragment>
                );
              })}
            </div>
          </div>

          <CardContent>
            {renderStepContent()}
          </CardContent>

          {/* Navigation */}
          <div className="px-6 pb-6 flex justify-between">
            <Button
              variant="outline"
              onClick={handleBack}
              disabled={currentStep === 0}
            >
              <ChevronLeft className="h-4 w-4 mr-2" />
              Back
            </Button>
            
            {currentStep < STEPS.length - 1 ? (
              <Button onClick={handleNext} className="bg-purple-600 hover:bg-purple-700">
                Next
                <ChevronRight className="h-4 w-4 ml-2" />
              </Button>
            ) : (
              <Button 
                onClick={handleSubmit} 
                disabled={submitting}
                className="bg-green-600 hover:bg-green-700"
              >
                {submitting ? (
                  <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Creating...</>
                ) : (
                  <>{t('accommodation.createListing.publish')}</>
                )}
              </Button>
            )}
          </div>
        </Card>
      </div>
    </div>
  );
};

export default CreateListingPage;
